import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Container,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography, Link
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useSnackbar } from 'components/snackbar';
import { useAuth } from 'hooks/useAuthContext';
import { getAllAdminsAndCompanies, deleteUser } from 'api/addadminandcompany'
import DeleteIcon from '@mui/icons-material/Delete';
import { LoadingButton } from '@mui/lab';
import AddUsersComponent from 'components/AddUsers';
import EditIcon from '@mui/icons-material/Edit';


import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

/**
 * Component to manage admins and companies
 * 
 * This component fetches the list of admins and companies and displays them in a table.
 * It also allows the user to add, edit and delete users.
 * 
 * @returns {JSX.Element}
 */
const UserManageComponent = () => {
    const { user } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const addUserRef = useRef(null);

    const [isFetched, setIsFetched] = useState(false);
    const [isClick, setIsClick] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState({});
    const [open, setOpen] = useState(false);
    const [isOpen, setIsOpen] = useState(true);
    const [itemId, setItemId] = useState('');

    useEffect(() => {
        if (isEditOpen || isClick) {
            // Scroll to AddUsersComponent
            addUserRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [isEditOpen, isClick, selectedItem]);


    useEffect(() => {

        fetchjobs();
    }, []);

    /**
     * Fetches the admins, companies and recruiters and updates the
     * corresponding state variables. If the server's response is not
     * successful, it logs an error message. Finally, it sets the
     * isLoadingClick state to false and the isFetched state to true.
     * @returns {Promise<void>}
     */
    const fetchjobs = async () => {
        try {
            const response = await getAllAdminsAndCompanies({ id: user?.id, userType: user?.user_type });
            if (response && response?.success && response?.data) {
                if (response?.data?.admin) {
                    setAdmins(response?.data?.admin);
                }
                if (response?.data?.company) {
                    setCompanies(response?.data?.company);
                }
                if (response?.data?.recruiter) {
                    setRecruiter(response?.data?.recruiter);
                }
            }
            setIsClick(false)
            setIsFetched(true);
        } catch (error) {
            console.error('Error fetching jobs:', error);
        }
    };

    /**
     * Closes the confirmation dialog for deleting a user
     * and resets the item id to empty string
     */
    const handleClose = () => {
        setOpen(false);
        setItemId('');
    };
    /**
     * Closes the modal for changing a user's password
     */
    const handleClosePasswordModal = () => {
        setIsOpen(false);
    };
    const [admins, setAdmins] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [recruiter, setRecruiter] = useState([]);


    /**
     * Deletes a user and updates the state based on the user's type
     * 
     * If the user is an admin, the state admins is updated.
     * If the user is a company, the state companies is updated.
     * If the user is a recruiter, the state recruiter is updated.
     * 
     * In all cases, the confirmation dialog is closed and a snackbar is shown with the server's response message.
     * If the server's response is not successful, the snackbar shows an error message.
     */
    const handleDeleteUser = async () => {
        const response = await deleteUser({ id: itemId });
        if (response && response?.success) {
            if (response?.data && response.data?.user_type === 'admin') {
                const existingIndex = admins.findIndex(item => item._id === response.data?._id);
                if (existingIndex !== -1) {
                    const updatedAdmins = [...admins];
                    updatedAdmins.splice(existingIndex, 1);
                    setAdmins([...updatedAdmins]);
                }
            } else if (response?.data && response.data?.user_type === 'company') {
                const existingIndex = companies.findIndex(item => item._id === response.data?._id);
                if (existingIndex !== -1) {
                    const updatedCompany = [...companies];
                    updatedCompany.splice(existingIndex, 1);
                    setCompanies([...updatedCompany]);
                }
            } else {
                const existingIndex = recruiter.findIndex(item => item._id === response.data?._id);
                if (existingIndex !== -1) {
                    const updatedRecruiter = [...recruiter];
                    updatedRecruiter.splice(existingIndex, 1);
                    setRecruiter([...updatedRecruiter]);
                }
            }
            setOpen(false);
            setItemId('');
            enqueueSnackbar(response.message, { variant: 'success' });
        } else {
            enqueueSnackbar(response.message, { variant: 'error' });
            setItemId('');
        }
    };

    /**
     * Handles the edit user button click event. The function sets the selectedItem state to the data of the user to be edited, sets the isClick state to true, and sets the isEditOpen state to true. This triggers the opening of the edit user dialog with the selected user's data prefilled.
     * @param {Object} data - The data of the user to be edited.
     */
    const handleEditUser = async (data) => {
        setSelectedItem(data)
        setIsClick(true);
        setIsEditOpen(true)
    }

    /**
     * Handles the save user data event. The function sets the isClick state to false, and if the newData is not null, it checks the user type of the newData. If the user type is 'admin', it checks if the selectedItem is not null and if it contains a full_name property. If it does, it finds the index of the newData's _id in the admins array and if it exists, it updates the admins array by splicing the existing item and adding the newData at the beginning. If the selectedItem is null, it adds the newData to the beginning of the admins array. If the user type is 'company', it does the same as the above but for the companies array. If the user type is neither 'admin' nor 'company', it does the same as the above but for the recruiter array. Finally, it sets the isEditOpen state to false and sets the selectedItem state to an empty object.
     * @param {Object} newData - The data of the user to be saved.
     */
    const handleSaveData = (newData) => {
        console.log(newData);
        setIsClick(false);
        if (newData) {
            // If editing an existing user (selectedItem has data)
            if (selectedItem && selectedItem?.full_name) {
                // Check if user_type changed - if so, remove from old category
                if (selectedItem.user_type !== newData.user_type) {
                    // Remove from old category
                    if (selectedItem.user_type === 'admin') {
                        const existingIndex = admins.findIndex(item => item._id === newData._id);
                        if (existingIndex !== -1) {
                            const updatedAdmins = [...admins];
                            updatedAdmins.splice(existingIndex, 1);
                            setAdmins(updatedAdmins);
                        }
                    } else if (selectedItem.user_type === 'company') {
                        const existingIndex = companies.findIndex(item => item._id === newData._id);
                        if (existingIndex !== -1) {
                            const updatedCompany = [...companies];
                            updatedCompany.splice(existingIndex, 1);
                            setCompanies(updatedCompany);
                        }
                    } else {
                        const existingIndex = recruiter.findIndex(item => item._id === newData._id);
                        if (existingIndex !== -1) {
                            const updatedRecruiter = [...recruiter];
                            updatedRecruiter.splice(existingIndex, 1);
                            setRecruiter(updatedRecruiter);
                        }
                    }
                }

                // Add to new category or update in same category
                if (newData?.user_type === 'admin') {
                    const existingIndex = admins.findIndex(item => item._id === newData._id);
                    if (existingIndex !== -1) {
                        const updatedAdmins = [...admins];
                        updatedAdmins[existingIndex] = newData;
                        setAdmins(updatedAdmins);
                    } else {
                        setAdmins([newData, ...admins]);
                    }
                } else if (newData?.user_type === 'company') {
                    const existingIndex = companies.findIndex(item => item._id === newData._id);
                    if (existingIndex !== -1) {
                        const updatedCompany = [...companies];
                        updatedCompany[existingIndex] = newData;
                        setCompanies(updatedCompany);
                    } else {
                        setCompanies([newData, ...companies]);
                    }
                } else {
                    const existingIndex = recruiter.findIndex(item => item._id === newData._id);
                    if (existingIndex !== -1) {
                        const updatedRecruiter = [...recruiter];
                        updatedRecruiter[existingIndex] = newData;
                        setRecruiter(updatedRecruiter);
                    } else {
                        setRecruiter([newData, ...recruiter]);
                    }
                }
            } else {
                // Adding new user
                if (newData?.user_type === 'admin') {
                    setAdmins([newData, ...admins]);
                } else if (newData?.user_type === 'company') {
                    setCompanies([newData, ...companies]);
                } else {
                    setRecruiter([newData, ...recruiter]);
                }
            }
        }
        setIsEditOpen(false);
        setSelectedItem({});
        // fetchjobs();
    };


    return (
        <Box style={{ marginTop: '80px' }}>
            <Container>
                {isFetched ? <>
                    <Typography variant="h5" component="div" sx={{ marginBottom: '1rem' }}>
                        Manage Admins and Companies
                    </Typography>
                    <Box sx={{ margin: '2rem 0' }} ref={addUserRef}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                            <Typography variant="h5" component="div" sx={{ marginBottom: '1rem' }}>
                                Admin List
                            </Typography>
                            <Stack direction="row" spacing={2}>
                                <Link variant="contained" onClick={() => setIsClick(true)} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <AddCircleIcon /> <span>Add User</span>
                                </Link>
                            </Stack>
                        </Stack>
                        {isEditOpen || isClick ?
                                <AddUsersComponent onSaveData={handleSaveData} initialData={selectedItem} userOption={[
                                    { label: 'admin' },
                                    { label: 'company' },
                                    { label: 'recruiter' }
                                ]} /> : ''}
                        <Paper elevation={3}>
                            <div style={{ height: 'auto', width: '100%' }}>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Phone Number</TableCell>
                                                <TableCell>Email Address</TableCell>
                                                <TableCell>Password</TableCell>
                                                <TableCell>Edit</TableCell>
                                                <TableCell>Delete</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {admins.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={8}>No company admin added yet.</TableCell>
                                                </TableRow>
                                            ) : (
                                                admins.map((item, index) => (
                                                    <TableRow key={item._id || index}>
                                                        <TableCell>{item.full_name || 'N/A'}</TableCell>
                                                        <TableCell>{item.phone_number || 'N/A'}</TableCell>
                                                        <TableCell>{item.email || 'N/A'}</TableCell>
                                                        <TableCell style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.generated_password || item?.password}</TableCell>
                                                        <TableCell><EditIcon onClick={() => handleEditUser(item)} style={{ cursor: 'pointer' }} /></TableCell>
                                                        <TableCell key={item._id}>
                                                            <LoadingButton
                                                                fullWidth size="medium"
                                                                variant="contained"
                                                                textAlign={'right'}
                                                                endIcon={<DeleteIcon />}
                                                                onClick={() => {
                                                                    // handleDeleteUser(item._id);
                                                                    setItemId(item._id)
                                                                    setOpen(true);
                                                                }
                                                                }
                                                            >
                                                            </LoadingButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                        </Paper>
                    </Box>
                    <Box sx={{ margin: '2rem 0' }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                            <Typography variant="h5" component="div" sx={{ marginBottom: '1rem' }}>
                                Recruiter User List
                            </Typography>
                        </Stack>
                        <Paper elevation={3}>
                            <div style={{ height: 'auto', width: '100%' }}>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Company Name</TableCell>
                                                <TableCell>Phone Number</TableCell>
                                                <TableCell>Email Address</TableCell>
                                                <TableCell>Password</TableCell>
                                                <TableCell>Edit</TableCell>
                                                <TableCell>Delete</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {recruiter.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={8}>No recruiter user added yet.</TableCell>
                                                </TableRow>
                                            ) : (
                                                recruiter.map((item, index) => (
                                                    <TableRow key={item._id || index}>
                                                        <TableCell>{item?.recruiter_name || 'N/A'}</TableCell>
                                                        <TableCell>{item?.full_name || 'N/A'}</TableCell>
                                                        <TableCell>{item?.phone_number || 'N/A'}</TableCell>
                                                        <TableCell>{item?.email || 'N/A'}</TableCell>
                                                        <TableCell>{item?.generated_password || item?.password}</TableCell>
                                                        <TableCell><EditIcon onClick={() => handleEditUser(item)} style={{ cursor: 'pointer' }} /></TableCell>
                                                        <TableCell key={item._id}>
                                                            <LoadingButton
                                                                fullWidth size="medium"
                                                                variant="contained"
                                                                textAlign={'right'}
                                                                endIcon={<DeleteIcon />}
                                                                onClick={() => {
                                                                    // handleDeleteUser(item._id)}
                                                                    setItemId(item._id)
                                                                    setOpen(true);
                                                                }}
                                                            >
                                                            </LoadingButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                        </Paper>
                    </Box>
                    <Box sx={{ margin: '2rem 0' }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                            <Typography variant="h5" component="div" sx={{ marginBottom: '1rem' }}>
                                Company User List
                            </Typography>
                        </Stack>
                        <Paper elevation={3}>
                            <div style={{ height: 'auto', width: '100%' }}>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Phone Number</TableCell>
                                                <TableCell>Email Address</TableCell>
                                                <TableCell>Password</TableCell>
                                                <TableCell>Edit</TableCell>
                                                <TableCell>Delete</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {companies.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={8}>No company user added yet.</TableCell>
                                                </TableRow>
                                            ) : (
                                                companies.map((item, index) => (
                                                    <TableRow key={item._id || index}>
                                                        <TableCell>{item?.full_name || 'N/A'}</TableCell>
                                                        <TableCell>{item?.phone_number || 'N/A'}</TableCell>
                                                        <TableCell>{item?.email || 'N/A'}</TableCell>
                                                        <TableCell>{item?.generated_password || item?.password}</TableCell>
                                                        <TableCell><EditIcon onClick={() => handleEditUser(item)} style={{ cursor: 'pointer' }} /></TableCell>
                                                        <TableCell key={item._id}>
                                                            <LoadingButton
                                                                fullWidth size="medium"
                                                                variant="contained"
                                                                textAlign={'right'}
                                                                endIcon={<DeleteIcon />}
                                                                onClick={() => {
                                                                    setItemId(item._id)
                                                                    setOpen(true);
                                                                }}
                                                            >
                                                            </LoadingButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                        </Paper>
                    </Box>
                </> : null}
                <Dialog open={open} onClose={handleClose}>
                    <DialogTitle>Delete User</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete this user?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button onClick={handleDeleteUser} autoFocus>
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog isOpen={isOpen} onClose={handleClosePasswordModal}>
                    <DialogTitle>Display password</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Please copy this password this will not show again!
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClosePasswordModal}>Cancel</Button>
                        <Button onClick={handleClosePasswordModal} autoFocus>
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};

export default UserManageComponent;
